import React from "react";
import { useNavigateStore } from "../../stepFlowHooks/useNavigateStore";
import { useTranslation } from "react-i18next";
import { ERROR_STATE } from "../../constants";
import { getLegacyFlag } from "../../utils/toolbarItemUtils";
import ErrorAlert from "../../components/general/ErrorAlert";
import PresessionErrorModal from "../../components/browser/PresessionErrorModal";
import { ServiceException } from "@smithy/smithy-client";

enum ErrorName {
    RESOURCE_NOT_READY_EXCEPTION = "ResourceNotReadyException",
    ACCESS_DENIED_EXCEPTION = "AccessDeniedException",
    INTERNAL_SERVER_EXCEPTION = "InternalServerException",
}

const SuspendedPortalErrorMessage =
    "Cannot access resource because account is deleted";
const UserAccessLoggingBugErrorMessage = "V750620258";

interface BrowserCreationErrorModalProps {
    error: ServiceException & Error;
}

// TODO [LOWA-30534] Extracted from https://tiny.amazon.com/19xxsr90k
export const BrowserCreationErrorModal = ({
    error,
}: BrowserCreationErrorModalProps) => {
    const { t } = useTranslation();

    if (error == null) {
        return;
    }

    const errorName = error.name;
    const errorMessage = error.message;
    const requestId = error.$metadata?.requestId;

    if (errorName === ErrorName.RESOURCE_NOT_READY_EXCEPTION) {
        return (
            <ErrorModal
                errorState={ERROR_STATE.INFO}
                errorHeader={t("lease.errorAlert.pendingPortal.header")}
                errorMessage={t("lease.errorAlert.pendingPortal.message")}
                requestId={requestId}
            />
        );
    } else if (errorName === ErrorName.ACCESS_DENIED_EXCEPTION) {
        if (errorMessage?.includes(SuspendedPortalErrorMessage)) {
            return (
                <ErrorModal
                    errorState={ERROR_STATE.INFO}
                    errorHeader={t("lease.errorAlert.suspendedPortal.header")}
                    errorMessage={t("lease.errorAlert.suspendedPortal.message")}
                    requestId={requestId}
                />
            );
        }
    } else if (errorName === ErrorName.INTERNAL_SERVER_EXCEPTION) {
        if (errorMessage?.includes(UserAccessLoggingBugErrorMessage)) {
            return (
                <ErrorModal
                    errorState={ERROR_STATE.WARNING}
                    errorHeader={t(
                        "lease.errorAlert.userAccessLoggingBug.header"
                    )}
                    errorMessage={t(
                        "lease.errorAlert.userAccessLoggingBug.message"
                    )}
                    requestId={requestId}
                />
            );
        }
    }

    return (
        <ErrorModal
            errorState={ERROR_STATE.ERROR}
            errorHeader={t("lease.errorAlert.serverError.header")}
            errorMessage={t("lease.errorAlert.serverError.message")}
            requestId={requestId}
        />
    );
};

interface ErrorModalProps {
    errorState: ERROR_STATE;
    errorHeader: React.ReactNode;
    errorMessage: React.ReactNode;
    requestId?: string;
}

// TODO [LOWA-28380] Check `isLegacy` flag usage and clean up.
const ErrorModal = (props: ErrorModalProps) => {
    const { t } = useTranslation();
    const navigateToSignInStep = useNavigateStore(
        (store) => store.navigateToSignInStep
    );

    if (getLegacyFlag()) {
        return (
            <div className="awsui On-top">
                <ErrorAlert
                    type={props.errorState}
                    header={props.errorHeader}
                    message={props.errorMessage}
                    refreshButtonText={t("refresh.button.label")}
                    onRefreshButtonClick={() => window.location.reload()}
                    onDismissErrorAlert={() => {}}
                />
            </div>
        );
    }

    return (
        <PresessionErrorModal
            title={props.errorHeader}
            message={props.errorMessage}
            requestId={props.requestId}
            onDismiss={navigateToSignInStep}
        />
    );
};
